<svelte:options
  customElement={{
    tag: "oc-selection-tile-v2",
    extend: window.__components.extend({ useOcAutoAriaLabel: true }),
    props: {
      variant: { type: "String" },
      color: { type: "String" },
      imgSrc: { type: "String", attribute: "img-src" },
      widthBehavior: { type: "String", attribute: "width-behavior" },
      strikeThrough: { type: "Boolean", attribute: "strike-through" },
    },
  }}
/>

<script lang="ts">
  import ActionSlot from "../../../common/components/action/ActionSlot.svelte";
  import type { ActionState } from "../../../common/components/action/ActionSlotProps";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import type { Props } from "./SelectionTileV2.types.js";

  let {
    variant = "text",
    widthBehavior = "fit",
    color = undefined,
    imgSrc = undefined,
    strikeThrough = false,
  }: Props = $props();

  const Host = $host();

  let clientWidth = $state(0);
  let clientHeight = $state(0);
  let strikeThroughSize = $derived(
    strikeThrough ? `${Math.min(clientWidth, clientHeight)}px` : undefined,
  );

  let actionState = $state<ActionState>({});
</script>

<div
  class="selection-tile {`--variant-${variant}`} "
  style:--color={color}
  class:--checked={actionState.checked}
  class:--hover={actionState.hover}
  class:--disabled={actionState.disabled}
  class:--active={actionState.active}
  style:--strike-through-size={strikeThroughSize}
  style:--img-src={imgSrc && `url(${imgSrc})`}
  bind:clientWidth
  bind:clientHeight
>
  <ActionSlot {Host} bind:actionState></ActionSlot>
  <slot name="content" />
  {#if strikeThrough}
    <span class="strikethrough"> </span>
  {/if}
</div>

<DefaultStyles display="block" {widthBehavior} />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";

  :global {
    :host {
      /*        */
      --color: initial;

      position: relative;
      pointer-events: none;
    }

    .selection-tile {
      --show-overlay: initial;
      --overlay-color: initial;
      --offset: 0px;
      color: tokens.$oc-component-selection-tile-text-color;
      outline: #{tokens.$oc-component-selection-tile-border-color} solid
        #{tokens.$oc-component-selection-tile-border-width};
      outline-offset: var(--offset);
      overflow: hidden;

      /*                  */
      height: 100%;

      /*                                                    */
      &:after {
        content: var(--show-overlay);
        position: absolute;
        border-radius: inherit;
        inset: calc(-1 * var(--offset));
        background: var(--overlay-color);
        pointer-events: none;
      }
    }

    .--variant-container,
    .--variant-text {
      border-radius: tokens.$oc-component-selection-tile-roundrect-border-radius;
      background: tokens.$oc-component-selection-tile-background-color;
    }

    .--variant-text {
      padding: 12px 16px;
      font: tokens.$oc-component-selection-tile-text-font;
    }

    .--variant-color,
    .--variant-image {
      width: tokens.$oc-component-selection-tile-round-size;
      height: tokens.$oc-component-selection-tile-round-size;
      border-radius: 50%;
    }

    .--variant-color {
      background: var(--color); /*                                                                */
    }

    .--variant-image {
      background-image: var(--img-src);
      background-position: center;
      background-size: 100%;
    }

    /*            */
    .--checked {
      --offset: 2px;
      outline-color: #{tokens.$oc-component-selection-tile-border-color-selected};
      outline-width: #{tokens.$oc-component-selection-tile-border-width-selected};
    }

    /*          */
    .--hover {
      --offset: 1px;
      --overlay-color: #{tokens.$oc-component-interactive-overlay-background-color-hover};
      --show-overlay: "";
    }

    /*           */
    .--active {
      --offset: 1px;
      --overlay-color: #{tokens.$oc-component-interactive-overlay-background-color-active};
      --show-overlay: "";
    }

    /*             */
    .selection-tile.--disabled {
      outline-color: #{tokens.$oc-component-selection-tile-disabled-border-color};
      outline-width: #{tokens.$oc-component-selection-tile-disabled-border-width};
      --overlay-color: #{tokens.$oc-component-selection-tile-disabled-overlay};
      --show-overlay: "";
    }

    /*            */
    .strikethrough {
      padding: tokens.$oc-semantic-spacing-50;
      position: absolute;

      height: var(--strike-through-size);
      width: var(--strike-through-size);

      /*                       */
      inset: 0;
      margin: auto;

      &:after {
        content: "";
        position: absolute;
        border-radius: tokens.$oc-semantic-border-radius-200;
        right: tokens.$oc-semantic-spacing-50;
        top: tokens.$oc-semantic-spacing-50;

        height: calc(1.41 * (100% - 2 * #{tokens.$oc-semantic-spacing-50}));
        background: tokens.$oc-component-selection-tile-strike-through-color;
        width: tokens.$oc-component-selection-tile-strike-through-width;
        outline: tokens.$oc-component-selection-tile-strike-through-outline-color solid
          tokens.$oc-component-selection-tile-strike-through-outline-width;

        transform-origin: top left;
        transform: rotate(45deg);
      }
    }

    /*                             */
    .action-slot::slotted(input:not(:disabled)) {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      min-width: calc(1.5rem + 8px);
      min-height: calc(1.5rem + 8px);
    }
  }
</style>
